import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import './Home.css';

const Home = () => {
  const { user, verifyCode, sendEmailCode, verifyEmailCode } = useAuth();
  const [authMode, setAuthMode] = useState('email'); // 'phone' | 'email'
  // Phone state
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState(['', '', '', '']);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  // Email state
  const [emailValue, setEmailValue] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [showEmailCode, setShowEmailCode] = useState(false);
  const [emailCooldown, setEmailCooldown] = useState(0);
  // Shared
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const codeInputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Get referral code from URL
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      // Store in localStorage to preserve it
      localStorage.setItem('referralCode', refCode);
    } else {
      // Try to get from localStorage
      const storedRef = localStorage.getItem('referralCode');
      if (storedRef) {
        setReferralCode(storedRef);
      }
    }
  }, [searchParams]);

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 0) return '';
    if (numbers.length <= 3) return `(${numbers}`;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    if (numbers.length <= 8) return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 8)}-${numbers.slice(8, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const formatted = formatPhone(value);
    setPhone(formatted);
    setError('');
  };

  const handleCodeChange = async (index, value) => {
    const digit = value.replace(/\D/g, '').slice(0, 1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (digit && index < 3) {
      codeInputRefs[index + 1].current?.focus();
    }

    // Auto-submit when last digit is entered
    if (digit && index === 3) {
      const codeString = newCode.join('');
      if (codeString.length === 4) {
        setLoading(true);
        const phoneNumbers = '7' + phone.replace(/\D/g, '');
        const result = await verifyCode(phoneNumbers, codeString, referralCode);
        
        setLoading(false);
        
        if (result.success) {
          localStorage.removeItem('referralCode');
          navigate('/dashboard');
        } else {
          setError(result.message || 'Неверный код');
        }
      }
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputRefs[index - 1].current?.focus();
    }
  };

  const handleCodePaste = async (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    const newCode = ['', '', '', ''];
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);
    
    // Auto-submit if 4 digits pasted
    if (pastedData.length === 4) {
      setLoading(true);
      const phoneNumbers = '7' + phone.replace(/\D/g, '');
      const result = await verifyCode(phoneNumbers, pastedData, referralCode);
      
      setLoading(false);
      
      if (result.success) {
        localStorage.removeItem('referralCode');
        navigate('/dashboard');
      } else {
        setError(result.message || 'Неверный код');
        codeInputRefs[3].current?.focus();
      }
    } else if (pastedData.length > 0) {
      codeInputRefs[pastedData.length].current?.focus();
    }
  };

  const sendSms = async (phoneNumbers) => {
    try {
      const response = await axios.post('/api/auth/send-sms', { phone: phoneNumbers });
      if (response.data.success) {
        return { success: true };
      }
      return { success: false, message: response.data.message || 'Ошибка отправки SMS' };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Ошибка отправки SMS',
        cooldown: err.response?.data?.cooldown
      };
    }
  };

  const startPhoneCooldown = (seconds) => {
    setResendCooldown(seconds);
    const iv = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(iv); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!phone.trim()) {
      setError('Введите номер телефона');
      return;
    }

    setLoading(true);
    const phoneNumbers = '7' + phone.replace(/\D/g, '');
    const result = await sendSms(phoneNumbers);
    setLoading(false);

    if (result.success) {
      setShowCodeInput(true);
      startPhoneCooldown(45);
    } else {
      setError(result.message || 'Ошибка отправки SMS');
      if (result.cooldown) startPhoneCooldown(result.cooldown);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const codeString = code.join('');
    if (codeString.length !== 4) {
      setError('Введите 4-значный код');
      return;
    }

    setLoading(true);
    const phoneNumbers = '7' + phone.replace(/\D/g, '');
    const result = await verifyCode(phoneNumbers, codeString, referralCode);
    
    setLoading(false);
    
    if (result.success) {
      localStorage.removeItem('referralCode');
      navigate('/dashboard');
    } else {
      setError(result.message || 'Неверный код');
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setCode(['', '', '', '']);
    setLoading(true);
    const phoneNumbers = '7' + phone.replace(/\D/g, '');
    const result = await sendSms(phoneNumbers);
    setLoading(false);
    if (result.success) {
      startPhoneCooldown(45);
    } else {
      setError(result.message);
      if (result.cooldown) startPhoneCooldown(result.cooldown);
    }
  };

  const startEmailCooldown = (seconds) => {
    setEmailCooldown(seconds);
    const iv = setInterval(() => {
      setEmailCooldown((prev) => {
        if (prev <= 1) { clearInterval(iv); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await sendEmailCode(emailValue);
    setLoading(false);
    if (result.success) {
      setShowEmailCode(true);
      startEmailCooldown(45);
    } else {
      setError(result.message);
      if (result.cooldown) startEmailCooldown(result.cooldown);
    }
  };

  const handleEmailCodeSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (emailCode.length !== 6) { setError('Введите 6-значный код'); return; }
    setLoading(true);
    const result = await verifyEmailCode(emailValue, emailCode);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message);
    }
  };

  const handleResendEmailCode = async () => {
    if (emailCooldown > 0) return;
    setError('');
    setEmailCode('');
    setLoading(true);
    const result = await sendEmailCode(emailValue);
    setLoading(false);
    if (result.success) {
      startEmailCooldown(45);
    } else {
      setError(result.message);
      if (result.cooldown) startEmailCooldown(result.cooldown);
    }
  };

  const switchMode = (mode) => {
    setAuthMode(mode);
    setError('');
    setShowCodeInput(false);
    setCode(['', '', '', '']);
    setShowEmailCode(false);
    setEmailCode('');
  };

  return (
    <div className="home-jyk">
      <section className="hero-jyk">
        <div className="hero-content-jyk">
          <h1 className="hero-title-jyk">NaixVPN</h1>

          {!user && (
            <>
              {/* Auth mode tabs */}
              <div className="auth-tabs-jyk">
                <button
                  type="button"
                  className={`auth-tab-jyk${authMode === 'email' ? ' auth-tab-active-jyk' : ''}`}
                  onClick={() => switchMode('email')}
                >
                  Email
                </button>
                <button
                  type="button"
                  className={`auth-tab-jyk${authMode === 'phone' ? ' auth-tab-active-jyk' : ''}`}
                  onClick={() => switchMode('phone')}
                >
                  Телефон
                </button>
              </div>

              {/* Email auth */}
              {authMode === 'email' && (
                !showEmailCode ? (
                  <form className="phone-form-jyk" onSubmit={handleEmailSubmit}>
                    <input
                      type="email"
                      className="email-input-jyk"
                      placeholder="your@email.com"
                      value={emailValue}
                      onChange={(e) => { setEmailValue(e.target.value); setError(''); }}
                      required
                      disabled={loading}
                      autoComplete="email"
                    />
                    <button type="submit" className="phone-submit-jyk" disabled={loading}>
                      {loading ? 'Отправка...' : 'Получить код'}
                    </button>
                    {error && <div className="error-message-jyk">{error}</div>}
                  </form>
                ) : (
                  <form className="phone-form-jyk" onSubmit={handleEmailCodeSubmit}>
                    <div className="code-hint-jyk" style={{ marginBottom: '15px', fontSize: '14px', color: '#888', textAlign: 'center' }}>
                      Введите 6-значный код из письма на {emailValue}
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="email-input-jyk"
                      placeholder="000000"
                      maxLength={6}
                      value={emailCode}
                      onChange={(e) => { setEmailCode(e.target.value.replace(/\D/g, '')); setError(''); }}
                      required
                      disabled={loading}
                      autoFocus
                      style={{ letterSpacing: '10px', fontSize: '1.5rem', textAlign: 'center' }}
                    />
                    <button type="submit" className="phone-submit-jyk" disabled={loading || emailCode.length !== 6}>
                      {loading ? 'Проверка...' : 'Войти'}
                    </button>
                    {error && <div className="error-message-jyk">{error}</div>}
                    <div className="resend-code-buttons-jyk">
                      <button
                        type="button"
                        className="resend-code-jyk"
                        onClick={handleResendEmailCode}
                        disabled={loading || emailCooldown > 0}
                      >
                        {emailCooldown > 0 ? `Отправить снова (${emailCooldown}с)` : 'Отправить код снова'}
                      </button>
                      <button
                        type="button"
                        className="change-phone-jyk"
                        onClick={() => { setShowEmailCode(false); setEmailCode(''); setError(''); }}
                        disabled={loading}
                      >
                        Изменить email
                      </button>
                    </div>
                  </form>
                )
              )}

              {/* Phone login form */}
              {authMode === 'phone' && (
                !showCodeInput ? (
                  <form className="phone-form-jyk" onSubmit={handlePhoneSubmit}>
                    <div className="phone-input-wrapper-jyk">
                      <span className="phone-prefix-jyk">+7</span>
                      <input
                        type="tel"
                        className="phone-input-jyk"
                        placeholder="(999) 999-99-99"
                        value={phone}
                        onChange={handlePhoneChange}
                        required
                        disabled={loading}
                      />
                    </div>
                    <button type="submit" className="phone-submit-jyk" disabled={loading}>
                      {loading ? 'Отправка...' : 'Получить доступ'}
                    </button>
                    {error && <div className="error-message-jyk">{error}</div>}
                  </form>
                ) : (
                  <form className="phone-form-jyk" onSubmit={handleCodeSubmit}>
                    <div className="code-hint-jyk" style={{ marginBottom: '15px', fontSize: '14px', color: '#888', textAlign: 'center' }}>
                      Введите 4-значный код из SMS
                    </div>
                    <div className="code-inputs-wrapper-jyk" onPaste={handleCodePaste}>
                      {code.map((digit, index) => (
                        <input
                          key={index}
                          ref={codeInputRefs[index]}
                          type="text"
                          className="code-input-jyk"
                          value={digit}
                          onChange={(e) => handleCodeChange(index, e.target.value)}
                          onKeyDown={(e) => handleCodeKeyDown(index, e)}
                          maxLength="1"
                          required
                          disabled={loading}
                          inputMode="numeric"
                          autoFocus={index === 0}
                        />
                      ))}
                    </div>
                    <button type="submit" className="phone-submit-jyk" disabled={loading}>
                      {loading ? 'Проверка...' : 'Войти'}
                    </button>
                    {error && <div className="error-message-jyk">{error}</div>}
                    <div className="resend-code-buttons-jyk">
                      <button
                        type="button"
                        className="resend-code-jyk"
                        onClick={handleResendCode}
                        disabled={loading || resendCooldown > 0}
                      >
                        {resendCooldown > 0 ? `Отправить снова (${resendCooldown}с)` : 'Отправить SMS снова'}
                      </button>
                      <button
                        type="button"
                        className="change-phone-jyk"
                        onClick={() => {
                          setShowCodeInput(false);
                          setCode(['', '', '', '']);
                          setError('');
                        }}
                        disabled={loading}
                      >
                        Изменить номер
                      </button>
                    </div>
                  </form>
                )
              )}
            </>
          )}

          {user && (
            <div className="hero-buttons-jyk">
              <Link to="/dashboard" className="btn-jyk btn-primary-jyk">
                Личный кабинет
              </Link>
            </div>
          )}

          <div className="footer-links-jyk">
            <button type="button" className="footer-link-jyk"  onClick={() => { window.location.href = "https://t.me/NaixVPN_bot"; }}>
              <svg className="footer-icon-jyk" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8" cy="8" r="1"></circle>
                <circle cx="12" cy="8" r="1"></circle>
                <circle cx="16" cy="8" r="1"></circle>
                <path d="M8 14h.01M12 14h.01M16 14h.01"></path>
                <path d="M8 17h8"></path>
              </svg>
              Наш бот
            </button>
            <button type="button" className="footer-link-jyk" onClick={() => { window.location.href = "https://t.me/naixgame_admin"; }}>
              <svg className="footer-icon-jyk" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                <path d="M13 8H7"></path>
                <path d="M17 12H7"></path>
              </svg>
              Тех поддержка
            </button>
          </div>
        </div>
      </section>

      <footer className="legal-footer-jyk">
      <div className="legal-info-jyk">
        <p>STIX LIMITED LIABILITY COMPANY (LLC)</p>
        <p>Рег.номер 278.110.1445669</p>
        <p>165 V. Atshemyan str., Erebuni 0005</p>
        <p>Yerevan, Armenia</p>
        <p className="legal-links-jyk">
          <a href="http://naix.cc/refund.html" target="_blank" rel="noopener noreferrer">Политика возврата</a>
          {' · '}
          <a href="https://naix.cc/privacy.html" target="_blank" rel="noopener noreferrer">Политика конфиденциальности</a>
          {' · '}
          <a href="https://naix.cc/oferta.html" target="_blank" rel="noopener noreferrer">Публичная оферта</a>
        </p>
      </div>
    </footer>

    </div>
  );
};

export default Home;
